<template>
  <div class="container">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Reportes</h1>
          <p class="view-header__subtitle">Elegí un reporte para empezar. Pasá el cursor sobre el ícono de ayuda para ver qué muestra cada uno.</p>
        </div>
      </div>
    </div>

    <div
      v-for="section in visibleSections"
      :key="section.title"
      class="reports-index__section"
    >
      <div class="reports-index__section-head">
        <span class="reports-index__section-icon"><i :class="`bi ${section.icon}`"></i></span>
        <h2 class="reports-index__section-title">{{ section.title }}</h2>
      </div>

      <div class="reports-index__grid">
        <router-link
          v-for="report in section.reports"
          :key="report.to"
          :to="report.to"
          class="reports-index__card"
        >
          <span class="reports-index__card-icon"><i :class="`bi ${report.icon}`"></i></span>
          <div class="reports-index__card-body">
            <h3 class="reports-index__card-title">
              {{ report.name }}
              <i
                class="bi bi-question-circle reports-index__card-help"
                :title="report.help"
              ></i>
            </h3>
            <p class="reports-index__card-desc">{{ report.help }}</p>
          </div>
          <i class="bi bi-arrow-right-short reports-index__card-go"></i>
        </router-link>
      </div>
    </div>

    <div v-if="visibleSections.length === 0" class="reports-index__empty">
      No tenés reportes habilitados con tus permisos actuales.
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      salesclerkModule: 0,
      // Estructura declarativa de secciones y reportes.
      // perm: permiso requerido (se chequea con $permitsAny).
      // companies: restringe a ciertos company_id.
      // salesclerk: requiere que el módulo de vendedores esté activo.
      sections: [
        {
          title: 'Ventas',
          icon: 'bi-graph-up-arrow',
          reports: [
            { name: 'Visión general de ventas', to: '/reports/sales', icon: 'bi-bar-chart-line', perm: 'REPORTE_VENTAS', help: 'Evolución y totales de ventas en el período seleccionado.' },
            { name: 'Listado de recibos', to: '/reports/reportReceipt', icon: 'bi-receipt', perm: 'REPORTE_VENTAS', help: 'Detalle de los recibos emitidos con sus medios de pago.' },
            { name: 'Ranking por producto', to: '/reports/ranking', icon: 'bi-trophy', perm: 'INVENTARIO', help: 'Productos más vendidos ordenados por cantidad o importe.' },
          ],
        },
        {
          title: 'Fiscal',
          icon: 'bi-bank',
          reports: [
            { name: 'IVA ventas', to: '/reports/ivasales', icon: 'bi-file-earmark-text', perm: 'REPORTE_IVA_VENTAS', help: 'Libro de IVA ventas para la liquidación fiscal.' },
            { name: 'IVA compras', to: '/reports/ivapurchases', icon: 'bi-file-earmark-text', perm: 'REPORTE_IVA_COMPRAS', help: 'Libro de IVA compras para la liquidación fiscal.' },
            { name: 'Reporte Z', to: '/reports/reportz', icon: 'bi-receipt-cutoff', perm: 'REPORTE_VENTAS', help: 'Cierre Z diario por punto de venta con ventas, neto, IVA por alícuota y percepciones.' },
          ],
        },
        {
          title: 'Compras',
          icon: 'bi-cart',
          reports: [
            { name: 'Visión general de compras', to: '/reports/purchases', icon: 'bi-bar-chart-line', perm: 'REPORTE_COMPRAS', help: 'Evolución y totales de compras en el período seleccionado.' },
            { name: 'Listado de órdenes de pago', to: '/reports/reportPayorder', icon: 'bi-cash-stack', perm: 'REPORTE_COMPRAS', help: 'Detalle de las órdenes de pago emitidas a proveedores.' },
          ],
        },
        {
          title: 'Inventario',
          icon: 'bi-box-seam',
          reports: [
            { name: 'Stock mínimo', to: '/reports/stock', icon: 'bi-exclamation-triangle', perm: 'REPORTE_STOCK_MINIMO', help: 'Productos cuyo stock cayó por debajo del mínimo configurado.' },
            { name: 'Stock valorizado', to: '/reports/stockvalued', icon: 'bi-currency-dollar', perm: 'REPORTE_STOCK_VALORIZADO', help: 'Valor del inventario actual a precio de costo.' },
            { name: 'Movimientos de stock', to: '/reports/stockmovements', icon: 'bi-arrow-left-right', perm: 'INVENTARIO', help: 'Historial de entradas y salidas de stock por producto.' },
          ],
        },
        {
          title: 'Fondos',
          icon: 'bi-wallet2',
          reports: [
            { name: 'Movimientos de caja', to: '/reports/funds', icon: 'bi-cash-coin', perm: 'REPORTE_MOVIMIENTO_FONDO_VALORIZADO', help: 'Ingresos y egresos de las cajas en el período.' },
            { name: 'Consolidado anual — Ventas vs. Egresos', to: '/reports/Consolidado', icon: 'bi-calendar3', perm: 'REPORTE_MOVIMIENTO_FONDO_VALORIZADO', companies: [337, 320], help: 'Comparativa anual de ventas contra egresos.' },
            { name: 'Consolidado anual — Cajas vs. Egresos', to: '/reports/Consolidated', icon: 'bi-calendar3', perm: 'REPORTE_MOVIMIENTO_FONDO_VALORIZADO', companies: [337, 320], help: 'Comparativa anual de cajas contra egresos.' },
          ],
        },
        {
          title: 'Cuentas corrientes de clientes',
          icon: 'bi-people',
          reports: [
            { name: 'Saldos a la fecha', to: '/reports/cccbalancestodate', icon: 'bi-journal-text', perm: 'CLIENTES_CTA_CORRIENTE', help: 'Saldo de cuenta corriente de cada cliente a la fecha.' },
          ],
        },
        {
          title: 'Cuentas corrientes de proveedores',
          icon: 'bi-truck',
          reports: [
            { name: 'Saldos a la fecha', to: '/reports/pacbalancestodate', icon: 'bi-journal-text', perm: 'PROVEEDORES_CTA_CORRIENTE', help: 'Saldo de cuenta corriente de cada proveedor a la fecha.' },
          ],
        },
        {
          title: 'Vendedores',
          icon: 'bi-person-badge',
          reports: [
            { name: 'Ranking de ventas', to: '/reports/salesclerksranking', icon: 'bi-trophy', salesclerk: true, help: 'Ranking de vendedores según sus ventas.' },
            { name: 'Resumen de cuentas por vendedor', to: '/reports/cccbysalesclerk', icon: 'bi-clipboard-data', salesclerk: true, help: 'Resumen de cuentas corrientes agrupadas por vendedor.' },
          ],
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      authUser: 'authUser/getUser',
    }),
    visibleSections() {
      return this.sections
        .map((section) => ({
          ...section,
          reports: section.reports.filter((report) => this.canSee(report)),
        }))
        .filter((section) => section.reports.length > 0);
    },
  },
  mounted() {
    this.getSalesclerkModule();
  },
  methods: {
    canSee(report) {
      if (report.perm && !this.$permitsAny(report.perm)) return false;
      if (report.companies && !report.companies.includes(this.authUser.company_id)) return false;
      if (report.salesclerk && this.salesclerkModule !== 1) return false;
      return true;
    },
    getSalesclerkModule() {
      this.$http
        .get('/api/users/getAuthUser', {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        })
        .then((response) => {
          this.salesclerkModule = response.data.data.branchoffices[0].options.salesclerk_module;
        })
        .catch((error) => {
          console.error('getSalesclerkModule:', error);
        });
    },
  },
};
</script>
